import { useState } from 'react';
import { X, Maximize2, Save, Loader2, Edit3, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
} from '../ui/dialog';
import { devLog } from '../../utils/formatters';

// Editable Cell Component
function EditableCell({ value, onSave, placeholder, className = "", fieldType = "currency" }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    let finalValue = editValue;
    
    // Convert dollars to cents for price fields
    if (fieldType === "currency" && finalValue) {
      finalValue = Math.round(parseFloat(finalValue) * 100);
    } else if (fieldType === "number" && finalValue) {
      finalValue = parseInt(finalValue) || 0;
    }
    
    onSave(finalValue);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(getDisplayValue());
      setIsEditing(false);
    }
  };

  const getDisplayValue = () => {
    if (fieldType === "currency") {
      return value ? (value / 100).toFixed(2) : "0.00";
    } else {
      return value || 0;
    }
  };

  const getFormattedDisplayValue = () => {
    if (fieldType === "currency") {
      return value ? `$${(value / 100).toFixed(2)}` : '$0.00';
    } else {
      return value ? value.toLocaleString() : '0';
    }
  };

  if (isEditing) {
    return (
      <div className="relative z-50 p-1">
        <Input
          type="number"
          step={fieldType === "currency" ? "0.01" : "1"}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyPress}
          className="text-center w-full h-8 border-2 border-blue-500 bg-white shadow-lg text-sm"
          placeholder={placeholder}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div 
      onClick={() => {
        setEditValue(getDisplayValue());
        setIsEditing(true);
      }}
      className={`w-full h-8 flex items-center justify-center cursor-pointer hover:bg-blue-200 rounded transition-colors border border-transparent hover:border-blue-300 m-1 ${className}`}
      title="Click to edit"
    >
      <span className="font-medium text-gray-900 text-sm">
        {getFormattedDisplayValue()}
      </span>
    </div>
  );
}

export default function ExpandedPartsTable({ 
  open, 
  onClose, 
  parts_comparison, 
  changes, 
  onPartChange, 
  onSave, 
  saving,
  formatCurrency 
}) {
  const [localChanges, setLocalChanges] = useState(changes);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const handleLocalPartChange = (partId, field, value) => {
    devLog(`Local change: Part ${partId}, Field ${field}, Value ${value}`);
    
    const newChanges = { ...localChanges };
    const partChanges = newChanges[partId] || {};
    
    if (field === 'piece_price_cents') {
      partChanges.piece_price_cents = parseInt(value) || 0;
    } else if (field.startsWith('volume_')) {
      const year = field.replace('volume_', '');
      if (!partChanges.volumes) partChanges.volumes = {};
      partChanges.volumes[year] = parseInt(value) || 0;
    }
    
    newChanges[partId] = partChanges;
    
    setLocalChanges(newChanges);
    setHasLocalChanges(true);
  };

  const handleSave = () => {
    devLog('Saving changes:', localChanges);
    
    // Apply all local changes to parent
    Object.keys(localChanges).forEach(partId => {
      const partChanges = localChanges[partId];
      
      if (partChanges.piece_price_cents !== undefined) {
        onPartChange(partId, 'piece_price_cents', partChanges.piece_price_cents);
      }
      if (partChanges.volumes) {
        Object.keys(partChanges.volumes).forEach(year => {
          onPartChange(partId, `volume_${year}`, partChanges.volumes[year]);
        });
      }
    });
    
    // Pass the localChanges directly to avoid timing issues
    onSave(localChanges);
    setHasLocalChanges(false);
  };

  const calculatePartScenarioRevenue = (part) => {
    const partChange = localChanges[part.part_id] || {};
    const price = partChange.piece_price_cents !== undefined 
      ? partChange.piece_price_cents 
      : part.scenario_piece_price_cents;
    
    const volumes = partChange.volumes || part.scenario_volumes;
    const totalVolume = Object.values(volumes).reduce((sum, vol) => sum + vol, 0);
    return price * totalVolume;
  };

  // Get years for volume columns - collect from ALL parts, not just the first one
  const volumeYears = (() => {
    const allYears = new Set();
    parts_comparison.forEach(part => {
      // Check both baseline and scenario volumes to get all possible years
      Object.keys(part.baseline_volumes || {}).forEach(year => allYears.add(year));
      Object.keys(part.scenario_volumes || {}).forEach(year => allYears.add(year));
    });
    return Array.from(allYears).sort();
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0">
        <div className="flex flex-col h-[95vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-white">
            <div className="flex items-center gap-3">
              <Maximize2 className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold">Scenario Parts Editor</h3>
            </div>
            <div className="flex items-center gap-2">
              {hasLocalChanges && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  title="Save your price and volume changes to this scenario"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Full Screen Table */}
          <div className="flex-1 p-4 bg-gray-50">
            <div className="bg-white rounded-lg border border-gray-200 h-full flex flex-col">
              {/* Table container with forced horizontal scroll */}
              <div className="flex-1 overflow-x-auto overflow-y-auto" style={{ maxWidth: 'calc(95vw - 2rem)' }}>
                <table className="text-sm" style={{ width: '2000px', minWidth: '2000px' }}>
                  <thead className="bg-gray-50 sticky top-0 z-30">
                    <tr>
                      <th className="text-left p-4 font-semibold border-r border-gray-200 sticky left-0 bg-gray-50 z-40" style={{width: '150px', minWidth: '150px'}}>Part Number</th>
                      <th className="text-left p-4 font-semibold border-r border-gray-200 sticky left-[150px] bg-gray-50 z-40" style={{width: '250px', minWidth: '250px'}}>Description</th>
                      <th className="text-center p-4 font-semibold border-r border-gray-200 bg-blue-50" style={{width: '150px', minWidth: '150px'}}>Price Baseline</th>
                      <th className="text-center p-4 font-semibold border-r border-gray-200 bg-blue-50" style={{width: '150px', minWidth: '150px'}}>Price Scenario</th>
                      {volumeYears.map(year => (
                        <>
                          <th key={`${year}-base`} className="text-center p-4 font-semibold border-r border-gray-200" style={{width: '150px', minWidth: '150px'}}>{year} Baseline</th>
                          <th key={`${year}-scen`} className="text-center p-4 font-semibold border-r border-gray-200 bg-blue-50" style={{width: '150px', minWidth: '150px'}}>{year} Scenario</th>
                        </>
                      ))}
                      <th className="text-center p-4 font-semibold bg-green-50" style={{width: '150px', minWidth: '150px'}}>Revenue Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts_comparison.map((part, index) => {
                      const partChange = localChanges[part.part_id] || {};
                      const scenarioRevenue = calculatePartScenarioRevenue(part);
                      const revenueDelta = scenarioRevenue - part.baseline_revenue_cents;
                      
                      return (
                        <tr key={part.part_id} className={`border-b hover:bg-gray-50 group ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                          <td className="p-3 font-medium border-r border-gray-200 sticky left-0 bg-white z-30" style={{width: '150px', minWidth: '150px'}}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm truncate">{part.part_number}</span>
                              {part.is_active && (
                                <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" title="Active for this part" />
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-gray-600 border-r border-gray-200 sticky left-[150px] bg-white z-30" style={{width: '250px', minWidth: '250px'}}>
                            <div className="text-sm truncate">{part.description}</div>
                          </td>
                          
                          {/* Piece Price - Baseline */}
                          <td className="text-center p-3 border-r border-gray-200" style={{width: '150px', minWidth: '150px'}}>
                            <div className="text-sm">
                              ${(part.baseline_piece_price_cents / 100).toFixed(2)}
                            </div>
                          </td>
                          
                          {/* Piece Price - Scenario (Editable) */}
                          <td className="p-1 bg-blue-50 border-r border-gray-200" style={{width: '150px', minWidth: '150px'}}>
                            <EditableCell
                              value={partChange.piece_price_cents !== undefined 
                                ? partChange.piece_price_cents 
                                : part.scenario_piece_price_cents
                              }
                              onSave={(value) => handleLocalPartChange(part.part_id, 'piece_price_cents', value)}
                              placeholder="Price"
                              fieldType="currency"
                              className="bg-transparent"
                            />
                          </td>
                          
                          {/* Volumes by Year */}
                          {volumeYears.map(year => {
                            const baselineVol = part.baseline_volumes[year] || 0;
                            const scenarioVol = partChange.volumes?.[year] !== undefined
                              ? partChange.volumes[year]
                              : (part.scenario_volumes[year] || 0);
                            
                            return (
                              <>
                                <td key={`${year}-base`} className="text-center p-3 border-r border-gray-200" style={{width: '150px', minWidth: '150px'}}>
                                  <div className="text-sm">
                                    {baselineVol.toLocaleString()}
                                  </div>
                                </td>
                                <td key={`${year}-scen`} className="p-1 bg-blue-50 border-r border-gray-200" style={{width: '150px', minWidth: '150px'}}>
                                  <EditableCell
                                    value={scenarioVol}
                                    onSave={(value) => handleLocalPartChange(part.part_id, `volume_${year}`, value)}
                                    placeholder="Volume"
                                    fieldType="number"
                                    className="bg-transparent"
                                  />
                                </td>
                              </>
                            );
                          })}
                          
                          {/* Revenue Delta */}
                          <td className={`text-center p-3 font-semibold bg-green-50 ${
                            revenueDelta >= 0 ? 'text-green-700' : 'text-red-700'
                          }`} style={{width: '150px', minWidth: '150px'}}>
                            <div className="text-sm">{formatCurrency(revenueDelta)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer with aggregated totals */}
          <div className="border-t bg-white p-4">
            <div className="grid grid-cols-3 gap-4 max-w-2xl">
              <div className="text-center">
                <div className="text-xs text-gray-500">Baseline Total</div>
                <div className="text-lg font-semibold text-gray-900">
                  {formatCurrency(parts_comparison.reduce((sum, p) => sum + p.baseline_revenue_cents, 0))}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">Scenario Total</div>
                <div className="text-lg font-semibold text-blue-900">
                  {formatCurrency(parts_comparison.reduce((sum, p) => sum + calculatePartScenarioRevenue(p), 0))}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">Total Impact</div>
                <div className={`text-lg font-semibold ${
                  parts_comparison.reduce((sum, p) => sum + (calculatePartScenarioRevenue(p) - p.baseline_revenue_cents), 0) >= 0 
                    ? 'text-green-700' : 'text-red-700'
                }`}>
                  {formatCurrency(parts_comparison.reduce((sum, p) => sum + (calculatePartScenarioRevenue(p) - p.baseline_revenue_cents), 0))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}